<script setup lang="ts">
  /**
   * This component dynamically renders the appropriate ad component based on the provided ad type and placement from props in AdSettings and $Filters(user geo/data).
   * It can be used universally in the code base instead of calling direct ad provider components for easier A/B testing.
   */
  import { computed, onMounted, ref, type Component } from 'vue'
  import Outbrain from '../AdOps/Outbrain.vue'
  import ArticleCard, {
    type ArticleCardProps,
  } from '../ArticleCard/ArticleCard.vue'
  import Taboola from '../AdOps/Taboola.vue'
  import Assertive from '../AdOps/Assertive.vue'
  import GenericAPIAd from '../AdOps/GenericAPIAd.vue'
  import Primis from '../AdOps/Primis.vue'
  import { createExtrnId } from '../../utils/string/uuid'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'
  import { adConfigStore } from '../../store/ad-ops/adConfigStore'
  import type { AdProps, AdProviderType } from '../../ad_ops/providers/types'

  const providerMap: Record<string, Component> = {
    assertive: Assertive,
    outbrain: Outbrain,
    taboola: Taboola,
    dplxapi: GenericAPIAd, // creative-based provider uses GenericAPIAd
    primis: Primis,
  }

  export interface AdHandlerProps {
    ad?: boolean
    placement: string
    adType?: AdProviderType
    engagedAdType?: AdProviderType | null
    adHeight?: string
    adWidth?: string
    design?: DesignVariant
    variant?: ArticleCardProps['variant']
    tilePosition?: number
    disableStyleWrapper?: boolean
    lazyRender?: boolean
    sticky?: boolean
  }

  const props = withDefaults(defineProps<AdHandlerProps>(), {
    ad: true,
    adType: 'programmatic',
    engagedAdType: null,
    adHeight: 'auto',
    adWidth: 'auto',
    design: 'msn',
    variant: 'default',
    tilePosition: -1,
    disableStyleWrapper: false,
    lazyRender: false,
    sticky: false,
  })

  const { isDevMode, devDisableAds } = useRuntimeConfig().public

  const adSettingsStore = useAdSettingsStore()
  const { data: adSettings } = storeToRefs(adSettingsStore)
  const adConfigs = adConfigStore()
  const userEngagement = userEngagementStore()

  const effectiveVariant = computed(() =>
    adSettings.value.enableSubtleGreyAdBackground
      ? 'subtle-greybg'
      : props.variant
  )
  const style = computed(() =>
    getArticleStyleVariants({ ...props, variant: effectiveVariant.value })
  )
  // The native widgets (Taboola, Outbrain) inject their loaders when their
  // component is created, which a server render cannot do. On a server
  // rendered page they wait behind a box of the same size until hydration,
  // then the real widget mounts. A client rendered page renders them straight
  // away, which is every tenant today.
  const nativeWidgetsReady = ref(!isBeforeHydration())
  onMounted(() => {
    nativeWidgetsReady.value = true
  })

  const { client } = await useOpenFeature()

  // Minted after mount, never in server HTML. The id is how the ad stack tells
  // impressions apart, and a page cached for a group would hand the same id to
  // everyone in it. Empty on both sides for the first render, so hydration
  // matches, then the key below remounts the provider with the real id, which
  // is why nothing in the ad stack sees an empty one.
  const adInstanceId = ref<string>('')
  onMounted(() => {
    adInstanceId.value = createExtrnId()
  })
  const isEngagedUser = userEngagement.isEngaged

  // engaged user override for ad type
  const finalAdType: AdProviderType = isEngagedUser
    ? props.engagedAdType || props.adType
    : props.adType

  const isValidAdProviderType = (value: string): value is AdProviderType => {
    return ['outbrain', 'taboola', 'assertive', 'dplxapi', 'primis'].includes(
      value
    )
  }

  // determine the ad provider based on ad type and feature flags
  const adProvider = computed((): AdProviderType => {
    switch (finalAdType) {
      case 'programmatic':
        return 'assertive'
      case 'native': {
        const nativeProvider = client.getStringValue(
          'nativeAdProvider',
          'outbrain'
        )
        // Ensure the feature flag value is a valid AdProviderType
        return isValidAdProviderType(nativeProvider)
          ? nativeProvider
          : 'outbrain'
      }
      default:
        return finalAdType || 'assertive'
    }
  })

  // get the specific ad config for the placement and provider
  const adProviderConfig = computed(() => {
    return adConfigs.getConfigForPlacement(props.placement, adProvider.value)
  })

  // determine which component to render based on the ad provider
  const component = computed(() => {
    return providerMap?.[adProvider.value] || ArticleCard
  })

  const BROWSER_ONLY_PROVIDERS: ReadonlySet<string> = new Set([
    'taboola',
    'outbrain',
  ])
  const renderProviderComponent = computed(
    () =>
      !BROWSER_ONLY_PROVIDERS.has(adProvider.value) || nativeWidgetsReady.value
  )

  const componentProps = computed(() => {
    const baseProps: AdProps = {
      ...props,
      ...(adProviderConfig.value || {}),
      adType: adProvider.value,
      class: props.disableStyleWrapper ? undefined : style.value.wrapper,
      style: style.value,
      adInstanceId: adInstanceId.value,
    }

    return {
      ...baseProps,
      variant: effectiveVariant.value,
    }
  })

  //style for the ad container
  const styles = computed(() => ({
    width: componentProps.value?.adWidth,
    height: componentProps.value?.adHeight,
    overflow: 'hidden',
    alignSelf: 'center',
    alignContent: 'center',
  }))

  const handleClick = () => {
    userEngagement.engage('ad')
  }
</script>

<template>
  <div
    v-if="devDisableAds && isDevMode"
    :key="componentProps.adInstanceId + '-dev'"
    v-bind="componentProps"
    aria-label="Partner Content"
    role="article"
    :style="styles"
    style="background-color: lightgray"
    @click="handleClick"
  >
    <code style="text-align: center">
      Placement: {{ props.placement }} | Provider: {{ adProvider }} | Ad Type:
      {{ props.adType }} | Size: {{ componentProps.adHeight }} x{{
        componentProps.adWidth
      }}
    </code>
  </div>
  <component
    :is="component"
    v-else-if="renderProviderComponent"
    :key="componentProps.adInstanceId"
    v-bind="componentProps"
    aria-label="Partner Content"
    role="article"
    :style="styles"
    @click="handleClick"
  />
  <div v-else :style="styles" data-testid="ad-placeholder" />
</template>
